import React, { useState, useEffect, useContext } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  makeStyles,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import { RiderContext } from "../../../context";
import { FilledSelect } from "../../../components/FormElements";
import { useTestIds } from "../../../hooks/useTestIds";

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: 320,
  },
  noScroll: {
    overflow: "hidden",
  },
}));

const AddPassModal = () => {
  const { t } = useTranslation();
  const { riderId } = useParams();
  const { fleets, fetchFleets, passOffers, fetchPassOffers, addPass } = useContext(RiderContext);
  const classes = useStyles();
  const history = useHistory();
  const testIds = useTestIds("rider-profile-add-pass-modal");

  const [isOpen, setIsOpen] = useState(true);
  const [selectedFleet, setSelectedFleet] = useState("");
  const [selectedPassOffer, setSelectedPassOffer] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!fleets?.length) {
      fetchFleets();
    }
  }, []);

  useEffect(() => {
    if (selectedFleet !== null) {
      fetchPassOffers(selectedFleet);
    }
    return () => fetchPassOffers(null);
  }, [selectedFleet]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const payload = {
      pass_offer_id: passOffers.find((p) => p.id === selectedPassOffer).id,
      customer: riderId,
      rides_remaining: passOffers.find((p) => p.id === selectedPassOffer).pass_offer_type.max_rides,
      disabled: false,
    };
    await addPass(payload);
    setSubmitting(false);
    handleClose();
  };

  const changeFleet = (event) => {
    setSelectedPassOffer("");
    setSelectedFleet(event.target.value);
  };

  const changePassOffer = (event) => {
    setSelectedPassOffer(event.target.value);
  };

  return (
    <Dialog
      data-testid={testIds.wrapper}
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle data-testid={testIds.title}>{t("Riders.Wallet.AddPass")}</DialogTitle>
      <DialogContent data-testid={testIds.content} className={classes.noScroll}>
        <Grid container spacing={4} justify="space-evenly" direction="column" alignItems="center">
          <Grid item xs={12}>
            <FormControl variant="outlined" className={classes.formControl}>
              <FilledSelect
                id="fleet"
                label={t("Riders.Wallet.Fleet")}
                value={selectedFleet}
                onChange={changeFleet}
                testIds={{
                  label: testIds.fleetSelectLabel,
                  select: testIds.fleetSelectInput,
                }}
              >
                {fleets.map((fleet) => (
                  <MenuItem key={fleet.uuid4} value={fleet.uuid4}>
                    {fleet.name}
                  </MenuItem>
                ))}
              </FilledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="outlined" className={classes.formControl}>
              <FilledSelect
                id="passOffer"
                label={t("Riders.Wallet.PassOffer")}
                value={selectedPassOffer}
                onChange={changePassOffer}
                SelectProps={{
                  disabled: !passOffers?.length,
                }}
                testIds={{
                  label: testIds.passOfferSelectLabel,
                  select: testIds.passOfferSelectInput,
                }}
              >
                {passOffers.map(
                  (pass) =>
                    // Must not be disabled and must have an end_date that is in the future
                    !pass.disabled &&
                    DateTime.now().toISO() < pass.end_date && (
                      <MenuItem key={pass.id} value={pass.id}>
                        {pass.pass_offer_type?.offer_type}
                      </MenuItem>
                    ),
                )}
              </FilledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography data-testid={testIds.warning}>
              {t("Riders.Wallet.AddPassWarning")}
            </Typography>
          </Grid>
        </Grid>
        {submitting && <LinearProgress color="secondary" />}
      </DialogContent>
      <DialogActions>
        <Button data-testid={testIds.cancelButton} onClick={handleClose}>
          {t("Riders.Wallet.Cancel")}
        </Button>
        <Button
          data-testid={testIds.saveButton}
          disabled={!selectedPassOffer || !selectedFleet}
          onClick={handleSubmit}
        >
          {t("Riders.Wallet.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPassModal;
